import { useAnalytics } from "@dashboard/components/ProductAnalytics/useAnalytics";
import {
  API_GUIDE_DOCS,
  API_REFERENCE_DOCS,
  EXTENDING_WITH_WEBHOOKS_DOCS_URL,
  EXTENSIONS_DOCS_URL,
  GIFT_CARD_PRODUCT_DOCS_URL,
  ORDER_MANAGEMENT_DOCS_URL,
  PRODUCT_CONFIGURATION_DOCS_URL,
  USER_PERMISSIONS_DOCS_URL,
} from "@dashboard/links";
import { type MessageDescriptor, useIntl } from "react-intl";

import { ContextualLine } from "./ContextualLine";
import { contextualLinks } from "./messages";

type SubtitleType =
  | "extending_saleor"
  | "product_list"
  | "order_list"
  | "dev_panel"
  | "gift_cards"
  | "staff_members"
  | "extensions";

const EVENT = "contextual_link_clicked";

/**
 * 타입별 부제목 본문. 아래 switch 가 고르는 것과 같은 항목이다. (2026-09-18 KST 추가)
 * 비어 있는지 미리 확인하려고 따로 뽑았다. 이유는 useContextualLink 안의 주석 참고.
 */
const subtitleMessages: Record<SubtitleType, MessageDescriptor> = {
  staff_members: contextualLinks.staffMembers,
  extending_saleor: contextualLinks.webhooks,
  dev_panel: contextualLinks.devModePanel,
  order_list: contextualLinks.orders,
  product_list: contextualLinks.products,
  gift_cards: contextualLinks.giftCards,
  extensions: contextualLinks.extensions,
};

export const useContextualLink = (type: SubtitleType) => {
  const { trackEvent: track } = useAnalytics();
  const intl = useIntl();
  const trackEvent = (type: string) => track(EVENT, { type });
  const message = subtitleMessages[type];

  // 문구를 비워 둔 항목은 부제목을 아예 내보내지 않는다. (2026-09-18 KST 추가)
  //
  // react-intl 은 빈 문자열을 '번역 없음' 으로 친다. 번역문도 defaultMessage 도 비어 있으면
  // 마지막 수단으로 메시지 아이디를 그대로 돌려주기 때문에(@formatjs/intl 의 message.js 끝줄
  // `return id`), 양쪽을 "" 로 비워도 화면에는 'tl1/1E' 같은 아이디가 찍힌다.
  // 문구를 되살리고 싶으면 messages.ts 의 defaultMessage 를 채우면 이 분기를 안 탄다.
  // 영어 로케일이거나 번역 파일을 불러오기 전에는 LocaleProvider 가 messages 를 undefined 로
  // 넘기므로 ?. 로 읽는다 (2026-10-02 KST 수정)
  if (!intl.messages?.[message.id as string] && !message.defaultMessage) {
    return null;
  }

  switch (type) {
    case "staff_members":
      return intl.formatMessage(contextualLinks.staffMembers, {
        userPermissions: (
          <ContextualLine.Link
            href={USER_PERMISSIONS_DOCS_URL}
            onClick={() => trackEvent("user_permissions_docs")}
          >
            {intl.formatMessage(contextualLinks.userPermissions)}
          </ContextualLine.Link>
        ),
      });
    case "extending_saleor":
      return intl.formatMessage(contextualLinks.webhooks, {
        extendingSaleor: (
          <ContextualLine.Link
            href={EXTENDING_WITH_WEBHOOKS_DOCS_URL}
            onClick={() => trackEvent("extending_saleor_docs")}
          >
            {intl.formatMessage(contextualLinks.extendingSaleor)}
          </ContextualLine.Link>
        ),
      });
    case "dev_panel":
      return intl.formatMessage(contextualLinks.devModePanel, {
        apiReference: (
          <ContextualLine.Link
            href={API_REFERENCE_DOCS}
            onClick={() => trackEvent("api_reference_docs")}
          >
            {intl.formatMessage(contextualLinks.apiReference)}
          </ContextualLine.Link>
        ),
        apiGuide: (
          <ContextualLine.Link href={API_GUIDE_DOCS} onClick={() => trackEvent("api_guide_docs")}>
            {intl.formatMessage(contextualLinks.apiGuide)}
          </ContextualLine.Link>
        ),
      });
    case "order_list":
      return intl.formatMessage(contextualLinks.orders, {
        orderManagement: (
          <ContextualLine.Link
            href={ORDER_MANAGEMENT_DOCS_URL}
            onClick={() => trackEvent("order_management_docs")}
          >
            {intl.formatMessage(contextualLinks.orderManagement)}
          </ContextualLine.Link>
        ),
      });
    case "product_list":
      return intl.formatMessage(contextualLinks.products, {
        productConfigurations: (
          <ContextualLine.Link
            href={PRODUCT_CONFIGURATION_DOCS_URL}
            onClick={() => trackEvent("product_configuration_docs")}
          >
            {intl.formatMessage(contextualLinks.productConfigurations)}
          </ContextualLine.Link>
        ),
      });
    case "gift_cards":
      return intl.formatMessage(contextualLinks.giftCards, {
        giftCards: (
          <ContextualLine.Link
            href={GIFT_CARD_PRODUCT_DOCS_URL}
            onClick={() => trackEvent("gift_card_product_docs")}
          >
            {intl.formatMessage(contextualLinks.giftCardsDocs)}
          </ContextualLine.Link>
        ),
      });
    case "extensions":
      return intl.formatMessage(contextualLinks.extensions, {
        extensions: (
          <ContextualLine.Link
            href={EXTENSIONS_DOCS_URL}
            onClick={() => trackEvent("extensions_docs")}
          >
            {intl.formatMessage(contextualLinks.extensionsDocs)}
          </ContextualLine.Link>
        ),
      });
    default:
      return null;
  }
};
